import { Head, router, usePage } from '@inertiajs/react'
import { Button, Group, NativeSelect, Paper, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { useState } from 'react'
import EmptyState from '@/components/EmptyState'
import PageLayout from '@/components/PageLayout'
import { SessionStatusBadge } from '@/components/badges'
import { submitHiddenForm } from '@/lib/formSubmission'
import { timeAgo } from '@/lib/session-utils'
import type { AgentShowPageProps, AgentSessionProps } from '@/types'

function SessionRow({ session }: { session: AgentSessionProps }) {
  return (
    <UnstyledButton onClick={() => router.visit(`/projects/${session.project_id}/chats?session=${session.id}`)}>
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={6} style={{ flex: 1 }}>
            <Text fw={500}>{session.title || session.preview || 'New conversation'}</Text>
            <Text c="dimmed" size="sm">
              Started {timeAgo(session.started_at)}{session.message_count > 0 ? ` - ${session.message_count} msgs` : ''}
            </Text>
          </Stack>
          <SessionStatusBadge status={session.status} />
        </Group>
      </Paper>
    </UnstyledButton>
  )
}

export default function AgentShow() {
  const { agent, sessions, projects, default_project_id, csrf_token } = usePage<AgentShowPageProps>().props
  const [selectedProjectId, setSelectedProjectId] = useState<string>(String(default_project_id || projects[0]?.id || ''))

  const handleNewChat = () => {
    submitHiddenForm({
      action: `/agents/${agent.id}/sessions`,
      values: {
        authenticity_token: csrf_token || '',
        project_id: selectedProjectId,
      },
    })
  }

  return (
    <>
      <Head title={agent.name} />

      <PageLayout
        title={agent.name}
        subtitle={agent.description || agent.role}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: agent.name },
        ]}
        actions={(
          <Group align="flex-end">
            <NativeSelect
              label="Project"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              data={projects.map((project) => ({ value: project.id, label: project.name }))}
            />
            <Button onClick={handleNewChat}>+ New Chat</Button>
          </Group>
        )}
      >
        <div>
          <Title order={3} mb="md">Sessions</Title>
          {sessions.length > 0 ? (
            <Stack gap="sm">
              {sessions.map((session) => (
                <SessionRow key={session.id} session={session} />
              ))}
            </Stack>
          ) : (
            <EmptyState>No sessions yet. Start a new chat to begin.</EmptyState>
          )}
        </div>
      </PageLayout>
    </>
  )
}
